import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { getBallroom, preloadBallroom } from '../ballroom-assets.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The photograph carries its own light.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'ballroom',
  family: 'photographic',
  danceability: 0.80,
  requiresTier: 0,
  energyLevel: 0.76,
  bpmMin: 100,
  bpmMax: 135,
  movement: 0.70,
  tonality: 0.75,
  prefers: { gridStrength: [0.60, 1.0] },
  enabled: true
};

// Ballroom: a 70s disco ballroom from two photographs - the dancefloor off
// and on. tools/build-ballroom.mjs cut their difference into 71 tiles with
// their row and column; the render is, per pixel,
//     colour = off + diff * intensity(tile of the pixel)
// so every tile is a bulb under glass on its own dimmer (30 ms up, 150 ms
// down), and the patterns are written in grid indices.
//
// THE MIRROR BALL IS THE DECISIVE DETAIL. Three hundred spots on screen: a
// pool of fixed directions on a Fibonacci sphere, ALL turning together about
// the vertical axis (one revolution in sixteen beats - one rigid sweep).
// Each is marched on the CPU from the ball's 3D position through the depth
// map Depth Anything gave the OFF picture, and lands where it meets the
// scene: wall, column, banquette, curtain, floor. The spots are then drawn
// IN THE PHOTO PASS, not as sprites: every pixel reconstructs its own 3D
// point from the depth map and sums the spots within a constant 3D radius
// of it, binned on a screen grid so a pixel only visits its cell's spots.
// A constant radius in the room is 10-24 px up front and 4-8 px at the back,
// and the footprint of a beam on an oblique surface is an ellipse, stretched
// along the beam's direction by 1 / cos of its incidence (the normal map).
//
// Two projectors from the top corners converge on the ball as volumetric
// cones (gaussian off the axis, brightest at the lyre, gone before the
// ball); on the bar's strong beat they cut for half a beat and the spots
// vanish. The ball itself wears a 24 x 12 facet grid that turns with the
// spots; a facet flashes when it sends a projector toward the camera.
//
// The camera model is the photo's (see warehouse.js): the lens at the
// origin, +z into the room, the horizon at the manifest's vanishing point,
// the camera hc = 1 - vanish.y above the floor and the frame's bottom at
// depth 1 - depth-scene.py's own convention, so the depth map agrees.

const TIER = {
  // `spots` is the budget on screen; a mirror ball throws most of its
  // reflections out of frame (one direction in seven lands on a surface the
  // camera sees), so `pool` directions are cast for each. `k` is how many
  // spots a pixel may sum (its cell's list).
  0: { spots: 80, pool: 560, k: 8, cones: false, facets: false },
  1: { spots: 150, pool: 1050, k: 12, cones: true, facets: false },
  2: { spots: 300, pool: 2100, k: 12, cones: true, facets: true }
};
const DEFAULT_TIER = 2;
const TILES_W = 128;              // the intensity table, one texel per tile id
const ATTACK_S = 0.030, RELEASE_S = 0.150;
const LIT_GAIN = 1.4;             // a lit tile's centre passes the bloom threshold
const TINT_AMOUNT = 0.20;         // the palette shifts the tiles' colour by this at most
const SPILL_GAIN = 0.9;
const GROUT_GAIN = 0.55;
const PATTERNS = ['checker', 'rows', 'ripple', 'random', 'chase', 'all'];
const RANDOM_SHARE = 0.40;
const CHASE_LEN = 6, CHASE_STEP = 2;    // tiles per beat
const BALL_BEATS_PER_TURN = 16;
const DROP_BEATS_PER_TURN = 4, DROP_FAST_BARS = 2;
const DROP_WHITE_BEATS = 1;
const CUT_BEATS = 0.5;
const SPOT_R3D = 0.017;           // constant radius in the room (frame height = 1 at depth 1): 24 px across up front at 720 lines, 4 at the back
const SPOT_Z_MIN = 0.9;           // nearer than this (the ceiling over the lens) a spot would flood the frame
const SPOT_CORE = 1.2;            // white-warm core, above the bloom threshold
const SPOT_WARM = [1.0, 0.93, 0.80];
const SPOT_TINT = 0.25;           // the palette on the spots
const SPOT_MAX_TEX = 512;         // the spot texture's width
const CELLS_X = 24, CELLS_Y = 14, CELL_SLOTS = 12;
const SAFE_FLOOR_SPOTS = 0.5;
const CONE_MAX = 0.35;
const FACET_LON = 24, FACET_LAT = 12;
const MARCH_STEPS = 16;
const MARCH_T_MAX = 12;
const FREE_BEAT_S = 0.5;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function checkPalette(count) {
  let seed = 12345;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const out = [];
  for (let id = 0; id <= count; id++) out.push([Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195), Math.floor(60 + rnd() * 195)]);
  return out;
}
function srgbToLin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }

// --- the patterns, pure: which tiles a beat lights. Exported for the tests.
export function motif(name, tiles, grid, beat, extra) {
  const out = new Map();
  if (name === 'checker') {
    for (const t of tiles) out.set(t.id, (t.row + t.col + beat) % 2 === 0 ? 1 : 0);
  } else if (name === 'rows') {
    const r = beat % grid.rows;                 // toward the camera: row 0 is the far row
    for (const t of tiles) out.set(t.id, t.row === r ? 1 : 0);
  } else if (name === 'ripple') {
    const c = extra.centre;
    let maxRing = 0;
    const ring = new Map();
    for (const t of tiles) { const d = Math.round(Math.hypot(t.row - c.row, t.col - c.col)); ring.set(t.id, d); if (d > maxRing) maxRing = d; }
    const k = beat % (maxRing + 1);
    for (const t of tiles) out.set(t.id, ring.get(t.id) === k ? 1 : 0);
  } else if (name === 'random') {
    const rnd = extra.rnd || Math.random;
    for (const t of tiles) out.set(t.id, rnd() < RANDOM_SHARE ? 1 : 0);
  } else if (name === 'chase') {
    const path = extra.path;
    const head = (beat * CHASE_STEP) % path.length;
    for (const t of tiles) out.set(t.id, 0);
    for (let k = 0; k < CHASE_LEN; k++) { const i = ((head - k) % path.length + path.length) % path.length; out.set(path[i], 1); }
  } else {
    for (const t of tiles) out.set(t.id, 1);
  }
  return out;
}
export function cheminSerpent(tiles) {
  const rows = new Map();
  for (const t of tiles) { if (!rows.has(t.row)) rows.set(t.row, []); rows.get(t.row).push(t); }
  const path = [];
  const keys = [...rows.keys()].sort((a, b) => a - b);
  keys.forEach((r, i) => {
    const l = rows.get(r).sort((a, b) => a.col - b.col);
    if (i % 2) l.reverse();
    for (const t of l) path.push(t.id);
  });
  return path;
}

// =============================================================================
// SHADERS
// =============================================================================
const photoVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
// mediump throughout, highp where the id is decoded and where the room is
// reconstructed (a depth read at half precision steps across the floor).
const photoFragmentShader = (K) => `
  precision mediump float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform highp sampler2D uIds;
  uniform sampler2D uTiles;       // 128 x 1: R intensity, GBA the tile's colour (linear)
  uniform sampler2D uCheck;       // 128 x 1: the build's check colours
  uniform highp sampler2D uDepth;
  uniform sampler2D uNormal;
  uniform highp sampler2D uSpots; // 512 x 2: row 0 = S.xyz, r ; row 1 = d.xyz, gain
  uniform highp sampler2D uCells; // (CELLS_X * 3) x CELLS_Y: four spot indices per texel, -1 empty
  uniform float uMean;
  uniform float uSpillGain;
  uniform vec3 uTint;
  uniform float uTintAmount;
  uniform float uWhite;           // the drop: every tile white
  uniform float uFlash;
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uIdDebug;
  uniform float uSolo;            // the spots alone, on black
  uniform float uSpotsOn;
  uniform vec4 uProj;             // vanish.x, vanish.y, photo aspect, hc
  uniform float uWMax;
  uniform vec3 uSpotColor;
  uniform float uSpotGain;
  uniform float uMaxK;
  uniform vec4 uCone0;            // lyre u, v ; ball u, v (image, v down)
  uniform vec4 uCone1;
  uniform vec2 uConeW;            // half width at the lyre, at the ball (image height units)
  uniform float uConeGain;
  uniform vec4 uBall;             // u, v, radius in u, radius in v
  uniform float uRot;
  uniform vec3 uL0;               // toward projector 0, ball space (z toward the camera)
  uniform vec3 uL1;
  uniform float uGlintGain;
  uniform float uSparkle;
  uniform float uSeed;
  uniform vec4 uHot;              // the two hot spots to paint over: u0, u1, v, radius (u)
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float hash21(vec2 p) { p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113); p += dot(p, p.yx + 1.9); return fract(p.x * p.y * 7.31); }
  // the safe zone, MULTIPLICATIVE, on the spots only (the brief)
  float safeAt(vec2 quadUv) {
    vec2 ndc = quadUv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR_SPOTS.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  highp float depthAt(vec2 img) {
    highp vec2 rg = texture2D(uDepth, vec2(img.x, 1.0 - img.y)).rg;
    return (256.0 * rg.x + rg.y) * (uWMax / 257.0);
  }
  highp vec3 pointOf(vec2 img, highp float w) {
    highp float z = 1.0 / max(w, 0.02);
    return vec3((img.x - uProj.x) * uProj.z * z, (uProj.y - img.y) * z, z);
  }
  // one spot's light at the room point P with surface normal n
  float spotAt(float idx, highp vec3 P, vec3 n) {
    highp vec2 tc = vec2((idx + 0.5) / ${SPOT_MAX_TEX.toFixed(1)}, 0.25);
    highp vec4 s = texture2D(uSpots, tc);
    vec4 dd = texture2D(uSpots, tc + vec2(0.0, 0.5));
    highp vec3 v = P - s.xyz;
    float r = s.w;
    // the beam's direction along the surface: the footprint stretches by
    // 1 / cos(incidence) that way
    vec3 d = dd.xyz;
    float cosI = clamp(abs(dot(n, d)), 0.33, 1.0);
    vec3 t = d - n * dot(d, n);
    float tl = length(t);
    t = tl > 1e-3 ? t / tl : vec3(0.0);
    float along = dot(v, t);
    highp vec3 perp = v - t * along;
    float dist2 = (along * cosI) * (along * cosI) + dot(perp, perp);
    float g = exp(-dist2 / (r * r) * 2.2);
    return g * dd.w;
  }
  float cone(vec4 c, vec2 img) {
    vec2 a = c.xy, b = c.zw;
    vec2 e = (b - a) * vec2(uProj.z, 1.0);
    vec2 p = (img - a) * vec2(uProj.z, 1.0);
    float l2 = dot(e, e);
    float s = clamp(dot(p, e) / l2, 0.0, 1.0);
    float q = length(p - e * s);
    float w = mix(uConeW.x, uConeW.y, s);
    float along = smoothstep(0.0, 0.04, s) * pow(1.0 - s, 1.3);
    return exp(-2.0 * (q / w) * (q / w)) * along;
  }
  void main() {
    vec2 uv = vUv * uUvScale + uUvOffset;
    vec2 img = vec2(uv.x, 1.0 - uv.y);
    highp vec4 idc = texture2D(uIds, uv);
    highp float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
    float w = idc.b;
    float kind = idc.a;             // 0 nothing, ~0.5 grout, 1 reflection
    vec4 td = texture2D(uTiles, vec2((id + 0.5) / ${TILES_W}.0, 0.5));
    if (uIdDebug > 0.5) {
      vec4 ck = texture2D(uCheck, vec2((id + 0.5) / ${TILES_W}.0, 0.5));
      float k = w > 0.999 ? 1.0 : 0.15 + 0.45 * w;
      vec3 c = ck.gba * k + vec3(step(0.9, kind) * 60.0 / 255.0);
      vec3 lo = c / 12.92;
      vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
      gl_FragColor = vec4(mix(lo, hi, step(0.04045, c)), 1.0);
      return;
    }
    float refl = step(0.9, kind);
    float grout = step(0.3, kind) * (1.0 - refl);
    // a reflection follows its tile entirely; a halo follows the mean more
    // the farther it is
    float I = mix(mix(uMean, td.r, w), td.r * uSpillGain, refl);
    vec3 off = texture2D(uOff, uv).rgb;
    vec3 diff = texture2D(uDiff, uv).rgb;
    vec3 tint = mix(vec3(1.0), uTint, uTintAmount);
    diff *= tint;
    float lum = dot(diff, vec3(0.2126, 0.7152, 0.0722));
    diff = mix(diff, vec3(lum * 1.7), uWhite);
    vec3 col = off + diff * I;
    // the grout: the tile's own colour spills over its edges
    col += td.gba * tint * td.r * w * grout * ${GROUT_GAIN.toFixed(2)};
    // the ball's hot spots, painted over with the ball just below them
    float hot = max(1.0 - length((img - uHot.xz) * vec2(1.0, 1.0 / uProj.z)) / uHot.w, 1.0 - length((img - uHot.yz) * vec2(1.0, 1.0 / uProj.z)) / uHot.w);
    if (hot > 0.0) {
      vec3 under = texture2D(uOff, uv - vec2(0.0, 0.035)).rgb * 0.9;
      col = mix(col, under, smoothstep(0.0, 0.5, hot));
    }
    if (uSolo > 0.5) col = vec3(0.0);
    // --- the spots: this pixel's point in the room, the spots of its cell
    if (uSpotsOn > 0.5) {
      highp float wd = depthAt(img);
      highp vec3 P = pointOf(img, wd);
      vec3 n = normalize(texture2D(uNormal, vec2(img.x, 1.0 - img.y)).rgb * 2.0 - 1.0);
      vec2 cell = floor(img * vec2(${CELLS_X.toFixed(1)}, ${CELLS_Y.toFixed(1)}));
      float acc = 0.0;
      for (int k = 0; k < ${K}; k++) {
        if (float(k) >= uMaxK) break;
        highp vec2 tc = vec2((cell.x * 3.0 + float(k / 4) + 0.5) / ${(CELLS_X * 3).toFixed(1)}, (cell.y + 0.5) / ${CELLS_Y.toFixed(1)});
        highp vec4 four = texture2D(uCells, tc);
        int j = k - (k / 4) * 4;
        float idx = j == 0 ? four.x : (j == 1 ? four.y : (j == 2 ? four.z : four.w));
        if (idx < 0.0) break;
        acc += spotAt(idx, P, n);
      }
      acc = min(acc, 1.3);
      col += uSpotColor * (acc * ${SPOT_CORE.toFixed(2)}) * uSpotGain * safeAt(vUv);
    }
    // --- the cones
    if (uConeGain > 0.0) {
      float c = cone(uCone0, img) + cone(uCone1, img);
      col += vec3(1.0, 0.95, 0.85) * c * ${CONE_MAX.toFixed(2)} * uConeGain * (1.0 - uSolo);
    }
    // --- the ball's facets
    if (uGlintGain > 0.0) {
      vec2 dq = (img - uBall.xy) / uBall.zw;
      float r2 = dot(dq, dq);
      if (r2 < 1.0) {
        vec3 nb = vec3(dq.x, -dq.y, sqrt(1.0 - r2));
        float lat = asin(clamp(nb.y, -1.0, 1.0));
        float lon = atan(nb.x, nb.z) + uRot;
        float fl = floor((lat / 3.14159 + 0.5) * ${FACET_LAT.toFixed(1)});
        float fo = floor(mod(lon / 6.2831853, 1.0) * ${FACET_LON.toFixed(1)});
        float clat = ((fl + 0.5) / ${FACET_LAT.toFixed(1)} - 0.5) * 3.14159;
        float clon = (fo + 0.5) / ${FACET_LON.toFixed(1)} * 6.2831853 - uRot;
        vec3 nf = vec3(cos(clat) * sin(clon), sin(clat), cos(clat) * cos(clon));
        // each facet is set a little askew (fixed per facet): the flashes
        // scatter over the ball instead of clustering on one highlight
        float h1 = hash21(vec2(fl, fo) + 0.37), h2 = hash21(vec2(fo, fl) + 0.71);
        vec3 askew = normalize(nf + vec3(h1 - 0.5, h2 - 0.5, 0.0) * 0.55);
        vec3 V = vec3(0.0, 0.0, 1.0);
        vec3 H0 = normalize(uL0 + V), H1 = normalize(uL1 + V);
        float g = step(0.945, max(dot(askew, H0), dot(askew, H1)));
        float flick = step(0.35 - 0.3 * uSparkle, hash21(vec2(fl + uSeed, fo)));
        vec2 cellf = fract(vec2((lat / 3.14159 + 0.5) * ${FACET_LAT.toFixed(1)}, mod(lon / 6.2831853, 1.0) * ${FACET_LON.toFixed(1)}));
        float inside = smoothstep(0.0, 0.08, min(min(cellf.x, 1.0 - cellf.x), min(cellf.y, 1.0 - cellf.y)));
        float rim = 1.0 - smoothstep(0.7, 1.0, r2);
        col += vec3(1.0, 0.95, 0.85) * g * flick * inside * rim * 0.9 * uGlintGain * (1.0 - uSolo);
      }
    }
    col += uFlash * vec3(0.35, 0.33, 0.30) * (1.0 - uSolo);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class BallroomVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._idDebug = urlParam('ballroomid') === '1';
    const one = urlParam('ballroomtile');
    this._oneTile = one != null && one !== '' && Number.isFinite(parseInt(one, 10)) ? parseInt(one, 10) : null;
    const spots = urlParam('ballroomspots');
    this._spotsOn = spots !== '0';
    this._solo = spots === 'solo';
    this._probe = urlParam('ballroomprobe') === '1';
    const exact = this._idDebug || this._oneTile != null || this._solo;
    this._exact = exact;
    this._postProcessing = exact
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.5, vignette: 0.25, aberration: 0.0008 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const assets = getBallroom();
    this._assets = assets;
    if (!assets) {
      const self = this;
      preloadBallroom().then(function (a) {
        if (!a || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose();
        self.init(s, c);
      });
    }
    const m = assets ? assets.manifest : { size: [1376, 768], vanish: { x: 0.5, y: 0.4 }, ball: { x: 0.5, y: 0.08, r: 0.044 }, projectors: [{ x: 0.26, y: 0.07 }, { x: 0.73, y: 0.07 }], hotSpots: null };
    const data = assets ? assets.data : { count: 0, rows: 0, cols: 0, tiles: [] };
    this._manifest = m;
    this._imageAspect = m.size[0] / m.size[1];
    this._tiles = data.tiles.map((t) => ({ id: t.id, row: t.row, col: t.col, cx: t.cx, cy: t.cy, color: t.color, lit: 0, target: 0 }));
    this._grid = { rows: data.rows || 1, cols: data.cols || 1 };
    this._count = data.count;
    this._objects = []; this._geometries = []; this._materials = []; this._textures = [];

    // --- the intensity table (R) with the tiles' colours (GBA), and the check palette
    this._tilesData = new Float32Array(TILES_W * 4);
    for (const t of this._tiles) { const c = t.color || [128, 128, 128]; this._tilesData[t.id * 4 + 1] = srgbToLin(c[0]); this._tilesData[t.id * 4 + 2] = srgbToLin(c[1]); this._tilesData[t.id * 4 + 3] = srgbToLin(c[2]); }
    this._tilesTex = new THREE.DataTexture(this._tilesData, TILES_W, 1, THREE.RGBAFormat, THREE.FloatType);
    this._tilesTex.minFilter = THREE.NearestFilter; this._tilesTex.magFilter = THREE.NearestFilter; this._tilesTex.generateMipmaps = false; this._tilesTex.needsUpdate = true;
    const pal = checkPalette(this._count);
    const checkData = new Float32Array(TILES_W * 4);
    for (let i = 0; i < TILES_W; i++) { const c = pal[i] || [0, 0, 0]; checkData[i * 4 + 1] = c[0] / 255; checkData[i * 4 + 2] = c[1] / 255; checkData[i * 4 + 3] = c[2] / 255; }
    this._checkTex = new THREE.DataTexture(checkData, TILES_W, 1, THREE.RGBAFormat, THREE.FloatType);
    this._checkTex.minFilter = THREE.NearestFilter; this._checkTex.magFilter = THREE.NearestFilter; this._checkTex.generateMipmaps = false; this._checkTex.needsUpdate = true;
    this._textures.push(this._tilesTex, this._checkTex);

    // --- the camera model
    const vx = m.vanish.x, vy = m.vanish.y;
    this._cam = { vx, vy, A: this._imageAspect, hc: 1 - vy };
    const depth = assets ? assets.depth : null;
    this._depth = depth; this._normal = assets ? assets.normal : null;
    const wBall = depth ? this._wAt(m.ball.x, m.ball.y) : 0.58;
    this._ball = this._pointOf(m.ball.x, m.ball.y, wBall);
    this._projectors = m.projectors.map((p) => this._pointOf(p.x, p.y, depth ? Math.max(0.2, this._wAt(p.x, p.y)) : 0.45));

    // --- the spots: a pool of fixed directions, a screen-space bin texture
    const pool = this._layout.pool;
    this._pool = pool; this._budget = this._layout.spots;
    this._dir = new Float32Array(pool * 3); this._seed = new Float32Array(pool);
    // The Fibonacci order runs from the top pole down: taken in that order,
    // the budget filled with the ceiling and the upper walls before a single
    // direction reached the floor. The pool is visited in a fixed shuffle.
    const phi = Math.PI * (3 - Math.sqrt(5));
    const order = []; for (let i = 0; i < pool; i++) order.push(i);
    let sh = 1234567; const rnd = () => { sh = (sh * 1103515245 + 12345) & 0x7fffffff; return sh / 0x7fffffff; };
    for (let i = pool - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); const t = order[i]; order[i] = order[j]; order[j] = t; }
    for (let k = 0; k < pool; k++) {
      const i = order[k];
      const y = 1 - (i + 0.5) / pool * 2, r = Math.sqrt(1 - y * y), a = phi * i;
      this._dir[k * 3] = Math.cos(a) * r; this._dir[k * 3 + 1] = y; this._dir[k * 3 + 2] = Math.sin(a) * r;
      this._seed[k] = rnd();
    }
    this._spotData = new Float32Array(SPOT_MAX_TEX * 2 * 4);
    this._spotTex = new THREE.DataTexture(this._spotData, SPOT_MAX_TEX, 2, THREE.RGBAFormat, THREE.FloatType);
    this._spotTex.minFilter = THREE.NearestFilter; this._spotTex.magFilter = THREE.NearestFilter; this._spotTex.generateMipmaps = false;
    this._cellData = new Float32Array(CELLS_X * 3 * CELLS_Y * 4).fill(-1);
    this._cellTex = new THREE.DataTexture(this._cellData, CELLS_X * 3, CELLS_Y, THREE.RGBAFormat, THREE.FloatType);
    this._cellTex.minFilter = THREE.NearestFilter; this._cellTex.magFilter = THREE.NearestFilter; this._cellTex.generateMipmaps = false;
    this._textures.push(this._spotTex, this._cellTex);
    this._cellCount = new Uint8Array(CELLS_X * CELLS_Y);
    this._hits = 0; this._spotImg = new Float32Array(this._budget * 2); this._spotAspect = new Float32Array(this._budget); this._spotZ = new Float32Array(this._budget);

    // --- the photo quad
    const hot = m.hotSpots || { u: [0.493, 0.510], v: 0.093, r: 0.009 };
    this._photoU = {
      uOff: { value: assets ? assets.off : null }, uDiff: { value: assets ? assets.diff : null }, uIds: { value: assets ? assets.ids : null },
      uTiles: { value: this._tilesTex }, uCheck: { value: this._checkTex },
      uDepth: { value: assets ? assets.depthTex : null }, uNormal: { value: assets ? assets.normalTex : null },
      uSpots: { value: this._spotTex }, uCells: { value: this._cellTex },
      uMean: { value: 0 }, uSpillGain: { value: SPILL_GAIN },
      uTint: { value: new THREE.Vector3(1, 1, 1) }, uTintAmount: { value: this._exact ? 0 : TINT_AMOUNT },
      uWhite: { value: 0 }, uFlash: { value: 0 },
      uUvScale: { value: new THREE.Vector2(1, 1) }, uUvOffset: { value: new THREE.Vector2(0, 0) },
      uIdDebug: { value: this._idDebug ? 1 : 0 }, uSolo: { value: this._solo ? 1 : 0 },
      uSpotsOn: { value: this._spotsOn && !!depth && (!this._exact || this._solo) ? 1 : 0 },
      uProj: { value: new THREE.Vector4(vx, vy, this._imageAspect, 1 - vy) }, uWMax: { value: assets && assets.manifest.depth ? assets.manifest.depth.wMax || 1.25 : 1.25 },
      uSpotColor: { value: new THREE.Vector3(SPOT_WARM[0], SPOT_WARM[1], SPOT_WARM[2]) }, uSpotGain: { value: 1 }, uMaxK: { value: this._layout.k },
      uCone0: { value: new THREE.Vector4(m.projectors[0].x, m.projectors[0].y, m.ball.x, m.ball.y) },
      uCone1: { value: new THREE.Vector4(m.projectors[1].x, m.projectors[1].y, m.ball.x, m.ball.y) },
      uConeW: { value: new THREE.Vector2(0.012, m.ball.r * this._imageAspect * 1.5) }, uConeGain: { value: this._layout.cones && !this._exact ? 1 : 0 },
      uBall: { value: new THREE.Vector4(m.ball.x, m.ball.y, m.ball.r, m.ball.r * this._imageAspect) }, uRot: { value: 0 },
      uL0: { value: new THREE.Vector3(0, 0, 1) }, uL1: { value: new THREE.Vector3(0, 0, 1) },
      uGlintGain: { value: this._layout.facets && !this._exact ? 1 : 0 }, uSparkle: { value: 0.5 }, uSeed: { value: 0 },
      uHot: { value: new THREE.Vector4(hot.u[0], hot.u[1], hot.v, hot.r) },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    for (let k = 0; k < 2; k++) {
      const d = new THREE.Vector3().subVectors(this._projectors[k], this._ball).normalize();
      this._photoU['uL' + k].value.set(d.x, d.y, -d.z);
    }
    const photoMat = new THREE.ShaderMaterial({ uniforms: this._photoU, vertexShader: photoVertexShader, fragmentShader: photoFragmentShader(this._layout.k), depthTest: false, depthWrite: false });
    this._photo = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), photoMat);
    this._photo.frustumCulled = false; this._photo.renderOrder = -10;
    this._objects.push(this._photo); this._geometries.push(this._photo.geometry); this._materials.push(photoMat);
    scene.add(this._photo);

    // --- behaviour
    this._time = 0; this._beatS = FREE_BEAT_S; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._beatsSincePhrase = 0; this._barAt = -1e9;
    this._low = 0.5; this._high = 0.5;
    this._rot = 0; this._rotDir = 1; this._fastUntil = -1e9; this._whiteUntil = -1e9; this._flashAt = -1e9;
    this._patternIdx = 0; this._pattern = PATTERNS[0]; this._extra = { centre: { row: 4, col: 4 }, rnd: Math.random, path: cheminSerpent(this._tiles) };
    this._lastPalette = undefined; this._scratch = new THREE.Color();
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastDropAt = -1e9; this._lastMode = null;
    this._applyBeat(0);
    this._applyPalette(null);
    this._writeTiles(0);
    this._reframe();
    if (this._photoU.uSpotsOn.value > 0) this._marchSpots();
  }

  // --- the camera model, CPU side
  _pointOf(u, v, w) {
    const z = 1 / Math.max(w, 0.02);
    return new THREE.Vector3((u - this._cam.vx) * this._cam.A * z, (this._cam.vy - v) * z, z);
  }
  _imageOf(P, out) { out.x = this._cam.vx + P.x / (this._cam.A * P.z); out.y = this._cam.vy - P.y / P.z; return out; }
  _wAt(u, v) {
    const d = this._depth; if (!d) return 0.5;
    const x = Math.min(d.width - 1.001, Math.max(0, u * d.width - 0.5)), y = Math.min(d.height - 1.001, Math.max(0, v * d.height - 0.5));
    const x0 = x | 0, y0 = y | 0, fx = x - x0, fy = y - y0;
    const w = d.w, W = d.width;
    const a = w[y0 * W + x0], b = w[y0 * W + x0 + 1], c = w[(y0 + 1) * W + x0], e = w[(y0 + 1) * W + x0 + 1];
    return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + e * fx) * fy;
  }
  _normalAt(u, v, out) {
    const nm = this._normal; if (!nm) return out.set(0, 0, -1);
    const x = Math.min(nm.width - 1, Math.max(0, Math.round(u * nm.width - 0.5))), y = Math.min(nm.height - 1, Math.max(0, Math.round(v * nm.height - 0.5)));
    const i = (y * nm.width + x) * 3;
    return out.set(nm.n[i], nm.n[i + 1], nm.n[i + 2]).normalize();
  }
  _ballRadius3d() { return this._manifest.ball.r * this._cam.A * this._ball.z; }

  // THE SPOTS: every direction of the pool, turned as one about the vertical
  // axis, marched from the ball through the depth map; the first `budget`
  // that land in frame are written to the spot texture and binned.
  _marchSpots() {
    const pool = this._pool, B = this._ball, budget = this._budget;
    const cosR = Math.cos(this._rot), sinR = Math.sin(this._rot);
    const d = this._tmpD || (this._tmpD = new THREE.Vector3());
    const P = this._tmpP || (this._tmpP = new THREE.Vector3());
    const img = this._tmpI || (this._tmpI = new THREE.Vector2());
    const sn = this._tmpSN || (this._tmpSN = new THREE.Vector3());
    const sd = this._spotData, cells = this._cellData, counts = this._cellCount;
    counts.fill(0); cells.fill(-1);
    let hits = 0;
    const r0 = this._ballRadius3d() * 1.2;
    for (let i = 0; i < pool && hits < budget; i++) {
      const fx = this._dir[i * 3], fy = this._dir[i * 3 + 1], fz = this._dir[i * 3 + 2];
      d.set(fx * cosR - fz * sinR, fy, fx * sinR + fz * cosR);
      if (d.z < -0.3) continue;                        // toward the camera: behind it
      // sixteen steps along the ray, then a short bisection
      let t = r0, tPrev = r0, hit = false;
      const step = (MARCH_T_MAX - r0) / MARCH_STEPS;
      for (let s = 0; s < MARCH_STEPS; s++) {
        P.copy(B).addScaledVector(d, t);
        if (P.z < 0.08) break;
        this._imageOf(P, img);
        if (img.x < 0 || img.x > 1 || img.y < 0 || img.y > 1) break;
        if (1 / P.z <= this._wAt(img.x, img.y)) { hit = true; break; }
        tPrev = t; t += step;
      }
      if (!hit) continue;
      let lo = tPrev, hi = t;
      for (let s = 0; s < 5; s++) {
        const mid = (lo + hi) / 2;
        P.copy(B).addScaledVector(d, mid); this._imageOf(P, img);
        if (1 / P.z <= this._wAt(img.x, img.y)) hi = mid; else lo = mid;
      }
      t = (lo + hi) / 2;
      P.copy(B).addScaledVector(d, t); this._imageOf(P, img);
      if (img.x < 0 || img.x > 1 || img.y < 0 || img.y > 1) continue;
      // not on the ball itself (the depth map holds it as a disc), not nearer than the lens's ceiling
      if (P.z < SPOT_Z_MIN) continue;
      const mb = this._manifest.ball;
      if (Math.hypot((img.x - mb.x), (img.y - mb.y) / this._cam.A) < mb.r * 1.15) continue;
      const r = SPOT_R3D * (0.85 + 0.3 * this._seed[i]);
      const k = hits;
      sd[k * 4] = P.x; sd[k * 4 + 1] = P.y; sd[k * 4 + 2] = P.z; sd[k * 4 + 3] = r;
      const o = (SPOT_MAX_TEX + k) * 4;
      sd[o] = d.x; sd[o + 1] = d.y; sd[o + 2] = d.z; sd[o + 3] = 0.75 + 0.25 * this._seed[i];
      // the probe's view of it: image position, ellipse aspect, depth
      this._normalAt(img.x, img.y, sn);
      this._spotImg[k * 2] = img.x; this._spotImg[k * 2 + 1] = img.y;
      this._spotAspect[k] = Math.min(3, 1 / Math.max(0.33, Math.abs(sn.dot(d))));
      this._spotZ[k] = P.z;
      // its cells: the screen box of its footprint (up to 4x stretched)
      const rImg = r / P.z * 1.8;
      const x0 = Math.max(0, Math.floor((img.x - rImg / this._cam.A) * CELLS_X)), x1 = Math.min(CELLS_X - 1, Math.floor((img.x + rImg / this._cam.A) * CELLS_X));
      const y0 = Math.max(0, Math.floor((img.y - rImg) * CELLS_Y)), y1 = Math.min(CELLS_Y - 1, Math.floor((img.y + rImg) * CELLS_Y));
      for (let cy = y0; cy <= y1; cy++) for (let cx = x0; cx <= x1; cx++) {
        const c = cy * CELLS_X + cx;
        const n = counts[c];
        if (n >= CELL_SLOTS) continue;
        cells[(cy * CELLS_X * 3 + cx * 3) * 4 + n] = k;
        counts[c] = n + 1;
      }
      hits++;
    }
    for (let k = hits; k < SPOT_MAX_TEX; k++) sd[k * 4 + 3] = 0;
    this._hits = hits;
    this._spotTex.needsUpdate = true; this._cellTex.needsUpdate = true;
  }

  // --- the frame: cover fit of the picture
  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    const u = this._photoU;
    if (aspect > this._imageAspect) {
      const k = this._imageAspect / aspect;
      u.uUvScale.value.set(1, k); u.uUvOffset.value.set(0, (1 - k) / 2);
    } else {
      const k = aspect / this._imageAspect;
      u.uUvScale.value.set(k, 1); u.uUvOffset.value.set((1 - k) / 2, 0);
    }
    this._fit = { sx: u.uUvScale.value.x, sy: u.uUvScale.value.y, ox: u.uUvOffset.value.x, oy: u.uUvOffset.value.y };
  }

  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : null;
    const t = this._photoU.uTint.value, s = this._photoU.uSpotColor.value;
    if (!vivid) { t.set(1, 1, 1); s.set(SPOT_WARM[0], SPOT_WARM[1], SPOT_WARM[2]); return; }
    this._scratch.set(vivid[0]);
    const c = this._scratch, mx = Math.max(c.r, c.g, c.b) || 1;
    t.set(c.r / mx, c.g / mx, c.b / mx);
    s.set(SPOT_WARM[0] * (1 - SPOT_TINT) + c.r / mx * SPOT_TINT, SPOT_WARM[1] * (1 - SPOT_TINT) + c.g / mx * SPOT_TINT, SPOT_WARM[2] * (1 - SPOT_TINT) + c.b / mx * SPOT_TINT);
  }

  onTrackChange(profile) { this._applyPalette(profile.artworkPalette); }

  _applyBeat(beat) {
    const targets = motif(this._pattern, this._tiles, this._grid, beat, this._extra);
    for (const t of this._tiles) t.target = targets.get(t.id) || 0;
  }
  _nextPattern() {
    let i; do { i = Math.floor(Math.random() * PATTERNS.length); } while (i === this._patternIdx && PATTERNS.length > 1);
    this._patternIdx = i; this._pattern = PATTERNS[i];
    if (this._pattern === 'ripple') { const t = this._tiles[Math.floor(Math.random() * this._tiles.length)]; this._extra.centre = { row: t.row, col: t.col }; }
  }

  _writeTiles(dt) {
    const data = this._tilesData;
    const breathe = this._pattern === 'all' ? 0.75 + 0.25 * Math.sin(this._time * Math.PI * 2 / 3) : 1;
    const gain = (0.7 + 0.5 * this._low) * breathe * LIT_GAIN;
    let sum = 0, n = 0;
    for (const t of this._tiles) {
      let v;
      if (this._oneTile != null) v = t.id === this._oneTile ? 1 : 0;
      else {
        const target = this._time < this._whiteUntil ? 1 : t.target;
        const k = target > t.lit ? dt / ATTACK_S : dt / RELEASE_S;
        t.lit += (target - t.lit) * Math.min(1, k);
        v = t.lit * (this._time < this._whiteUntil ? LIT_GAIN : gain);
      }
      data[t.id * 4] = v;
      sum += v; n++;
    }
    data[0] = 0;
    this._tilesTex.needsUpdate = true;
    this._photoU.uMean.value = this._oneTile != null ? 0 : (n ? sum / n : 0);
  }

  _updateDropSchedule(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (let i = 0; i < marks.length; i++) if (marks[i].type === 'drop' && marks[i].score >= 0.7) this._drops.push(marks[i].atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return null;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    const ref = this._lastNowMs != null ? Math.min(this._lastNowMs, now) : now;
    this._lastNowMs = now;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] < ref - 250) this._dropIdx++;
    if (this._dropIdx >= this._drops.length) return null;
    return (this._drops[this._dropIdx] - now) / 1000;
  }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return;   // gel
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const t = this._time;
    const director = profile.director || {};
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : 0;
    this._beatS = bpm ? 60 / bpm : FREE_BEAT_S;
    const beatS = this._beatS;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || {};
    const low = measured ? (bands.low || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    const k = Math.min(1, dt * 6);
    this._low += (low - this._low) * k; this._high += (high - this._high) * k;

    // --- clocks
    let beatEdge = false, barEdge = false, phraseEdge = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
      this._lastBeatPhase = profile.beatPhase;
    } else if (t - this._freeBeatAt >= beatS) { this._freeBeatAt = t; beatEdge = true; }
    if (beatEdge) this._beatCount++;
    if (profile.barPhase != null) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
      this._lastBarPhase = profile.barPhase;
    } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (beatEdge) this._beatsSincePhrase++;
    if (!phraseEdge && beatEdge && this._beatsSincePhrase >= 16) phraseEdge = true;
    if (phraseEdge) this._beatsSincePhrase = 0;
    this._lastPhrase = phrase;

    // --- the drop: all tiles white for a beat, the ball four times faster for two bars, a flash
    const sUntil = this._updateDropSchedule(profile);
    if (((sUntil != null && sUntil <= 0) || (measured && director.mode === 'drop' && this._lastMode !== 'drop')) && t - this._lastDropAt > 2) {
      this._lastDropAt = t; this._whiteUntil = t + DROP_WHITE_BEATS * beatS; this._fastUntil = t + DROP_FAST_BARS * 4 * beatS; this._flashAt = t;
    }
    this._lastMode = director.mode;
    if (phraseEdge) { this._nextPattern(); this._rotDir = -this._rotDir; }
    if (beatEdge) this._applyBeat(this._beatCount);
    if (barEdge) this._barAt = t;
    const sinceBar = t - this._barAt;
    const cutS = CUT_BEATS * beatS;
    const spotGain = this._exact && !this._solo ? 0 : (sinceBar < cutS ? 0 : Math.min(1, (sinceBar - cutS) / 0.08));

    // --- the ball turns (rate, not position), the spots with it
    const beatsPerTurn = t < this._fastUntil ? DROP_BEATS_PER_TURN : BALL_BEATS_PER_TURN;
    this._rot += this._rotDir * dt * Math.PI * 2 / (beatsPerTurn * beatS);
    this._rot = ((this._rot % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

    this._writeTiles(dt);
    const u = this._photoU;
    u.uWhite.value = t < this._whiteUntil ? 1 : 0;
    u.uFlash.value = Math.max(0, 1 - (t - this._flashAt) / 0.12) * (this._exact ? 0 : 1);
    if (u.uSpotsOn.value > 0) { this._marchSpots(); u.uSpotGain.value = spotGain; }
    u.uConeGain.value = this._layout.cones && !this._exact ? spotGain : 0;
    u.uGlintGain.value = this._layout.facets && !this._exact ? spotGain : 0;
    u.uRot.value = this._rot; u.uSparkle.value = this._high; u.uSeed.value = Math.floor(t * 12) % 61;
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();
    if (profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (this._probe && typeof window !== 'undefined') window.__ballroom = this.getProbe();
  }

  getProbe() {
    const spots = [];
    for (let k = 0; k < this._hits; k++) spots.push({ u: +this._spotImg[k * 2].toFixed(3), v: +this._spotImg[k * 2 + 1].toFixed(3), z: +this._spotZ[k].toFixed(2), aspect: +this._spotAspect[k].toFixed(2) });
    return {
      t: this._time, beat: this._beatCount, pattern: this._pattern, rot: this._rot, hits: this._hits, spotGain: this._photoU.uSpotGain.value,
      tiles: this._tiles.map((x) => ({ id: x.id, row: x.row, col: x.col, target: x.target, lit: +x.lit.toFixed(2) })), spots
    };
  }

  dispose() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    for (const tx of this._textures) tx.dispose();
    this._objects = []; this._geometries = []; this._materials = []; this._textures = [];
    if (typeof window !== 'undefined' && window.__ballroom) delete window.__ballroom;
    this._scene = null;
  }
}
